import React, { useEffect, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useKeepInViewport } from '../hooks/useKeepInViewport';
import { useBoundaries } from '../hooks/useBoundaries';
import * as fmt from '../lib/format';
import BoundaryDivider from './BoundaryDivider';

// Value format: "YYYY-Q" where Q is 1-4 (e.g. "2026-3").
interface QuarterPickerProps {
  value: string;
  onChange: (value: string) => void;
  max?: string;        // "YYYY-Q" inclusive upper bound (optional)
  className?: string;
}

const QUARTERS = [
  { q: 1, months: 'Jan – Mar' },
  { q: 2, months: 'Apr – Jun' },
  { q: 3, months: 'Jul – Sep' },
  { q: 4, months: 'Oct – Dec' },
];

export default function QuarterPicker({ value, onChange, max, className }: QuarterPickerProps) {
  const [open, setOpen] = useState(false);
  const [selYear, selQuarter] = value.split('-').map((n) => parseInt(n, 10));
  const [viewYear, setViewYear] = useState<number>(selYear || new Date().getFullYear());
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useKeepInViewport(open); // stays inside a narrow screen
  const boundaries = useBoundaries();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    // Escape closes it and puts focus back on the button, as for any popup.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const maxYear = max ? parseInt(max.split('-')[0], 10) : Infinity;
  const maxQuarter = max ? parseInt(max.split('-')[1], 10) : 4;
  const isDisabled = (y: number, q: number) => !!max && (y > maxYear || (y === maxYear && q > maxQuarter));

  // Quarters before the Jira conventions started are a section of their own.
  const isEarlier = (q: number) => !!boundaries && `${viewYear}-Q${q}` < boundaries.quarter;
  const sections = [
    { earlier: true, list: QUARTERS.filter(({ q }) => isEarlier(q)) },
    { earlier: false, list: QUARTERS.filter(({ q }) => !isEarlier(q)) },
  ].filter((section) => section.list.length > 0);

  const label = value ? fmt.quarter(`${selYear}-${selQuarter || 1}`) : 'Select quarter';

  const toggle = () => {
    if (!open) setViewYear(selYear || new Date().getFullYear());
    setOpen((o) => !o);
  };

  const pick = (q: number) => {
    if (isDisabled(viewYear, q)) return;
    onChange(`${viewYear}-${q}`);
    setOpen(false);
  };

  return (
    <div ref={ref} className={`relative ${className || ''}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        aria-haspopup="true"
        aria-expanded={open}
        className="inline-flex items-center gap-2 whitespace-nowrap rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-100"
      >
        <Calendar className="h-4 w-4 text-gray-400" />
        {label}
      </button>

      {open && (
        <div ref={popRef} role="group" aria-label="Choose a quarter" className="absolute right-0 z-50 mt-2 w-64 max-w-[calc(100vw-1rem)] rounded-2xl border border-gray-200 bg-white p-3 shadow-xl ring-1 ring-black/5">
          {/* Year navigator */}
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setViewYear((y) => y - 1)}
              className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100"
              aria-label="Previous year"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-semibold text-gray-900">{viewYear}</span>
            <button
              type="button"
              disabled={viewYear >= maxYear}
              onClick={() => setViewYear((y) => y + 1)}
              className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
              aria-label="Next year"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Quarter grid: the quarters before the Jira conventions started sit apart, under their own caption. */}
          {sections.map(({ earlier, list }) => (
            <div key={earlier ? 'before' : 'from'}>
              {boundaries && (earlier || sections.length > 1) && <BoundaryDivider label={earlier ? boundaries.label : boundaries.fromLabel} />}
              <div className="grid grid-cols-2 gap-1.5">
                {list.map(({ q, months }) => {
                  const selected = viewYear === selYear && q === selQuarter;
                  const disabled = isDisabled(viewYear, q);
                  return (
                    <button
                      key={q}
                      type="button"
                      disabled={disabled}
                      onClick={() => pick(q)}
                      className={`flex flex-col items-center rounded-lg px-2 py-2 text-sm font-medium transition-colors ${
                        selected
                          ? 'bg-blue-600 text-white shadow-sm'
                          : disabled
                          ? 'cursor-not-allowed text-gray-300'
                          : 'text-gray-700 hover:bg-blue-50 hover:text-blue-700'
                      }`}
                    >
                      <span>Q{q}</span>
                      <span className={`text-[0.6875rem] ${selected ? 'text-white' : 'text-gray-500'}`}>{months}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
